import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';

/**
 * API base — relative path /api/v1 so the browser treats all requests as
 * SAME-ORIGIN. In Docker, Caddy proxies /api/* from app.localhost to the
 * API container. In local dev, the Vite dev server proxies /api/* to
 * http://localhost:3001.
 *
 * Because requests are same-origin, the httpOnly refresh cookie (set with
 * path=/api/v1/auth) is automatically included by the browser on every
 * request to /api/v1/auth/*, including POST /auth/refresh.
 */
const API_BASE_URL = '/api/v1';

export const TOKEN_STORAGE_KEY = 'sb_access_token';

export interface AuthResponse {
  accessToken: string;
  user: {
    id: string;
    email: string;
    role: 'super_admin' | 'agency_admin' | 'client';
    tenantId: string;
    firstName: string | null;
    lastName: string | null;
  };
}

export const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
  timeout: 30_000,
});

// --- Request interceptor: inject Authorization Bearer token
api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = localStorage.getItem(TOKEN_STORAGE_KEY);
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ───── Token refresh machinery ─────────────────────────────────────

let refreshPromise: Promise<boolean> | null = null;

/**
 * Attempt to refresh the access token by calling POST /auth/refresh.
 * The httpOnly refresh cookie is sent automatically (same-origin).
 * Returns true if refresh succeeded, false otherwise.
 */
export async function refreshAccessToken(): Promise<boolean> {
  // Dedup concurrent refresh calls.
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    try {
      const r = await axios.post<AuthResponse>(
        `${API_BASE_URL}/auth/refresh`,
        {},
        { withCredentials: true },
      );
      localStorage.setItem(TOKEN_STORAGE_KEY, r.data.accessToken);
      localStorage.setItem('sb_user', JSON.stringify(r.data.user));
      // Notify React state listeners.
      window.dispatchEvent(
        new CustomEvent('auth:refresh', { detail: r.data.user }),
      );
      return true;
    } catch {
      return false;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

// ───── Response interceptor ────────────────────────────────────────

export interface ApiError {
  status: number;
  message: string;
  details?: unknown;
}

export function normalizeError(err: AxiosError): ApiError {
  const data = (err.response?.data ?? {}) as { message?: string; details?: unknown };
  const status = err.response?.status ?? 0;
  const message =
    data.message ||
    err.message ||
    'Unexpected network error';
  return { status, message, details: data.details };
}

api.interceptors.response.use(
  (r) => r,
  async (err: AxiosError) => {
    const norm = normalizeError(err);

    if (norm.status === 401 && typeof window !== 'undefined') {
      const requestUrl = err.config?.url || '';

      // Don't recurse: if the REFRESH endpoint itself failed, bail out.
      if (requestUrl.includes('/auth/refresh')) {
        const here = window.location.pathname + window.location.search;
        if (here.startsWith('/login') === false) {
          localStorage.removeItem(TOKEN_STORAGE_KEY);
          window.location.assign(`/login?next=${encodeURIComponent(here)}`);
        }
        return Promise.reject(norm);
      }

      // Try to refresh the token.
      const refreshed = await refreshAccessToken();
      if (refreshed) {
        // Retry the original request with the new token.
        const token = localStorage.getItem(TOKEN_STORAGE_KEY);
        const config = err.config;
        if (config && token) {
          config.headers.Authorization = `Bearer ${token}`;
          return api(config);
        }
      }

      // Refresh failed — redirect to login.
      const here = window.location.pathname + window.location.search;
      if (here.startsWith('/login') === false) {
        localStorage.removeItem(TOKEN_STORAGE_KEY);
        window.location.assign(`/login?next=${encodeURIComponent(here)}`);
      }
    }

    return Promise.reject(norm);
  },
);